import { useTranslation } from 'react-i18next'
import { Button, Row, Col, Input } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faGridHorizontal } from '@fortawesome/pro-solid-svg-icons/faGridHorizontal'
import { faClose } from '@fortawesome/pro-solid-svg-icons/faClose'
import { faCircleCheck } from '@fortawesome/pro-regular-svg-icons/faCircleCheck'
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons/faCheckCircle'
import { faPlus } from '@fortawesome/pro-solid-svg-icons/faPlus'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import {
  IQuestionBankAnswer,
  IQuestionBankQuestion,
} from 'interfaces/questionBank'
import { initialQuestionBankQuestion } from 'store/slices/initialData'
import {
  DragAndDrop,
  TableBody,
  Wrapper,
  Title,
  CorrectAnswer,
  RemoveAnswer,
} from './styled'

interface IProps {
  question: IQuestionBankQuestion
  setQuestion: (value: IQuestionBankQuestion) => void
}

const Answers = ({ question, setQuestion }: IProps) => {
  const { t } = useTranslation()
  const translationPrefix =
    'administration.blocks.type.questionBankV2.sections.manageQuestion.answers'

  const onDragEnd = (result: any) => {
    const { destination, source } = result

    if (!destination || destination.index === source.index) {
      return
    }

    const newAnswers = reorder(
      question.answers,
      source.index,
      destination.index
    )
    setQuestion({ ...question, answers: newAnswers })
  }

  const reorder = (
    list: IQuestionBankAnswer[],
    startIndex: number,
    endIndex: number
  ) => {
    const result = Array.from(list)
    const [removed] = result.splice(startIndex, 1)
    result.splice(endIndex, 0, removed)
    return result
  }

  const handleAnswerChange = (order: number, value: string) => {
    const newAnswers = question.answers.map((answer) =>
      answer.order === order ? { ...answer, name: value } : answer
    )
    setQuestion({ ...question, answers: newAnswers })
  }

  const handleAnswerRemove = (order: number) => {
    const newAnswers = question.answers.filter(
      (answer) => answer.order !== order
    )
    if (newAnswers.length === 0) {
      setQuestion({
        ...question,
        answers: [initialQuestionBankQuestion.answers[0]],
      })
    } else {
      setQuestion({
        ...question,
        answers: newAnswers.map((answer, index) => ({
          ...answer,
          order: index,
        })),
      })
    }
  }

  const handleAnswerCorrect = (order: number) => {
    const newAnswers = question.answers.map((answer) =>
      answer.order === order
        ? { ...answer, correct: true }
        : { ...answer, correct: false }
    )
    setQuestion({ ...question, answers: newAnswers })
  }

  const handleAnswerAdd = () => {
    const newAnswers = [
      ...question.answers,
      {
        ...initialQuestionBankQuestion.answers[0],
        order: question.answers.length,
      },
    ]
    setQuestion({ ...question, answers: newAnswers })
  }

  return (
    <Wrapper>
      <Title>{t(`${translationPrefix}.title`)}</Title>
      <TableBody>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="droppable">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {question.answers.map((answer, index) => (
                  <Draggable
                    key={index}
                    draggableId={index.toString()}
                    index={index}
                  >
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        style={{ ...provided.draggableProps.style }}
                      >
                        <Row
                          key={index}
                          type="flex"
                          style={{ backgroundColor: 'white' }}
                        >
                          <Col style={{ width: 40 }}>
                            <DragAndDrop>
                              <Button type="link" {...provided.dragHandleProps}>
                                <FontAwesomeIcon icon={faGridHorizontal} />
                              </Button>
                            </DragAndDrop>
                          </Col>
                          <Col style={{ flex: 1, display: 'grid' }}>
                            <Input
                              value={answer.name}
                              onChange={(e) =>
                                handleAnswerChange(answer.order, e.target.value)
                              }
                              placeholder={t(
                                `${translationPrefix}.placeHolder`
                              )}
                            />
                          </Col>
                          <Col style={{ width: 56 }}>
                            <CorrectAnswer correct={answer.correct}>
                              <Button
                                onClick={() =>
                                  handleAnswerCorrect(answer.order)
                                }
                              >
                                <FontAwesomeIcon
                                  icon={
                                    answer.correct
                                      ? faCheckCircle
                                      : (faCircleCheck as IconProp)
                                  }
                                />
                              </Button>
                            </CorrectAnswer>
                          </Col>
                          <Col style={{ width: 40 }}>
                            <RemoveAnswer>
                              <Button
                                onClick={() => handleAnswerRemove(answer.order)}
                              >
                                <FontAwesomeIcon icon={faClose} />
                              </Button>
                            </RemoveAnswer>
                          </Col>
                        </Row>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </TableBody>
      <Button onClick={handleAnswerAdd}>
        <FontAwesomeIcon icon={faPlus} />
        {t(`${translationPrefix}.add`)}
      </Button>
    </Wrapper>
  )
}

export default Answers
